// IncidentsPage — Accidents/Incidents list + create/edit/close + create-action
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, X, Save, ShieldAlert, Check, ListChecks, FileText, Camera, Trash2, Zap, ClipboardList, CalendarClock, Sparkles, Microscope, UserRound, Play, CheckCircle2, ClipboardCheck, PenLine, AlertTriangle, HardHat, Download, ArrowLeft, ThumbsUp, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import SignaturePad from "@/components/SignaturePad";
import { useOfflinePhotoQueue } from "@/hooks/useOfflinePhotoQueue";
import { PendingPhotoThumbs } from "@/components/PendingPhotoThumbs";
import { OtherDevicePendingBanner } from "@/components/OtherDevicePendingBanner";
import { clearOtherDevicePending } from "@/lib/photoFlusher";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import AccidentDashboard from "@/pages/safety/metrics/AccidentDashboard";
import CauseAnalysis from "@/pages/safety/metrics/CauseAnalysis";
import IncidentActions from "@/pages/safety/metrics/IncidentActions";
import SafetyCrossView from "@/pages/safety/metrics/SafetyCrossView";
import AccidentStatsRegister from "@/pages/safety/metrics/AccidentStatsRegister";
import { EMPTY_FILTERS } from "@/pages/safety/metrics/shared";

const SEVERITY_TONE = { Minor: "neutral", Moderate: "amber", Major: "red", Fatal: "red" };
const STATUS_TONE = { Reported: "amber", Investigating: "blue", "Pending review": "purple", Closed: "green" };
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);
const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}

export default function IncidentsPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [meta, setMeta] = useState({ types: [], severities: [], statuses: [] });
    const [q, setQ] = useState("");
    const [severityFilter, setSeverityFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [typeFilter, setTypeFilter] = useState("All");
    const [riddorOnly, setRiddorOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);   // null | "new" | <incident>
    const [view, setView] = useState("register"); // register | dashboard | safety-cross | causes | actions
    const [projects, setProjects] = useState([]);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const isPortal = !!portalClientId && !isInternalView;
    const [dashFilters, setDashFilters] = useState(() => ({ ...EMPTY_FILTERS, client_id: isPortal ? portalClientId : "" }));
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const backUrl = searchParams.get("back") || "";
    const [newPrefill, setNewPrefill] = useState(null);
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (portalClientId && !isInternalView) params.client_id = portalClientId;
            if (severityFilter !== "All") params.severity = severityFilter;
            if (statusFilter !== "All") params.status = statusFilter;
            if (typeFilter !== "All") params.incident_type = typeFilter;
            if (riddorOnly) params.riddor_only = true;
            const { data } = await api.get("/incidents", { params });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load incidents");
        }
        setLoading(false);
    }, [portalClientId, isInternalView, severityFilter, statusFilter, typeFilter, riddorOnly]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            const pid = searchParams.get("project");
            const cid = searchParams.get("client");
            const proj = projects.find((p) => p.id === pid);
            setNewPrefill({
                ...(cid ? { client_id: cid } : {}),
                ...(pid ? { project_id: pid } : {}),
                ...(proj?.building_ids?.[0] ? { building_id: proj.building_ids[0] } : {}),
                ...(proj?.site_ids?.[0] ? { site_id: proj.site_ids[0] } : {}),
            });
            setDialog("new");
            searchParams.delete("new"); setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams, projects]);
    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId && rows.length) {
            const r = rows.find((x) => x.id === openId);
            if (r) setDialog(r);
            searchParams.delete("open"); setSearchParams(searchParams, { replace: true });
        }
    }, [rows, searchParams, setSearchParams]);
    useEffect(() => {
        api.get("/incidents/meta").then((r) => setMeta(r.data)).catch(() => { });
        api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => { });
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => { });
    }, []);
    useEffect(() => {
        if (isPortal) setDashFilters((f) => ({ ...f, client_id: portalClientId }));
    }, [isPortal, portalClientId]);

    const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const buildingMap = useMemo(() => Object.fromEntries(buildings.map((b) => [b.id, b])), [buildings]);

    const filtered = rows.filter((r) => {
        if (!q) return true;
        const hay = `${r.reference || ""} ${r.location || ""} ${r.description || ""} ${clientMap[r.client_id]?.name || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
    });
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, {
        defaultKey: "occurred_at", defaultDir: "desc",
        accessors: {
            reference: (r) => r.reference || "",
            occurred_at: (r) => r.occurred_at || "",
            client: (r) => clientMap[r.client_id]?.name || "",
            location: (r) => r.location || "",
            severity: (r) => ({ Minor: 1, Moderate: 2, Major: 3, Fatal: 4 })[r.severity] || 0,
            status: (r) => r.status || "",
            riddor: (r) => !!r.riddor_reportable,
        },
    });

    async function closeIncident(inc) {
        if (!window.confirm("Close this incident? It will be marked Closed with timestamp.")) return;
        try {
            await api.post(`/incidents/${inc.id}/close`);
            toast.success("Incident closed");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Close failed");
        }
    }

    return (
        <div data-testid="incidents-page" className="space-y-5">
            {backUrl && (
                <button data-testid="incidents-back-to-project" onClick={() => navigate(backUrl)}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#3e4b63" }}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to project
                </button>
            )}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Safety &amp; Compliance
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Accidents &amp; Incidents</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} on file ·{" "}
                        <span className="font-semibold text-red-600">{rows.filter((r) => r.riddor_reportable).length} RIDDOR</span>{" · "}
                        <span className="font-semibold text-[#C68A1C]">{rows.filter((r) => r.status !== "Closed").length} open</span>
                    </p>
                </div>
                {canEdit && (
                    <button data-testid="incident-new-btn" onClick={() => setDialog("new")}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus size={15} /> Report incident
                    </button>
                )}
            </div>

            {/* View toggle */}
            <div data-testid="incident-view-toggle" className="inline-flex rounded-[9px] border border-[#E5E0DA] overflow-hidden flex-wrap">
                {[["register", "Register"], ["dashboard", "Metrics Dashboard"], ["safety-cross", "Safety Cross"], ["causes", "Cause Analysis"], ["actions", "Actions / Follow-up"], ["stats", "Accident Statistics"]].map(([id, label]) => (
                    <button key={id} data-testid={`incident-view-${id}`} onClick={() => setView(id)}
                        className={`px-4 py-1.5 text-[12.5px] font-semibold transition-colors ${view === id ? "text-white" : "text-[#6F6A64] hover:bg-[#F6F4F0]"}`}
                        style={view === id ? { background: "var(--brand-accent, #C75A1B)" } : undefined}>
                        {label}
                    </button>
                ))}
            </div>

            {view === "dashboard" && (
                <AccidentDashboard filters={dashFilters} onChange={setDashFilters} clients={clients}
                    buildings={buildings} projects={projects} meta={meta} isPortal={isPortal} />
            )}
            {view === "causes" && (
                <CauseAnalysis filters={dashFilters} onChange={setDashFilters} clients={clients}
                    buildings={buildings} projects={projects} meta={meta} isPortal={isPortal} canEdit={canEdit} />
            )}
            {view === "actions" && (
                <IncidentActions filters={dashFilters} onChange={setDashFilters} clients={clients}
                    buildings={buildings} projects={projects} meta={meta} isPortal={isPortal} />
            )}
            {view === "safety-cross" && (
                <SafetyCrossView initialClientId={isPortal ? portalClientId : (dashFilters.client_id || undefined)}
                    initialBuildingId={dashFilters.building_id || undefined} />
            )}
            {view === "stats" && (
                <AccidentStatsRegister clients={clients} isPortal={isPortal} portalClientId={portalClientId} canEdit={canEdit} />
            )}

            {view === "register" && (<>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968F]" />
                    <input data-testid="incident-search" placeholder="Search reference, location, description…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-80 focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                </div>
                <select data-testid="incident-severity-filter" value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="All">All severities</option>
                    {meta.severities.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="incident-status-filter" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="All">All statuses</option>
                    {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="incident-type-filter" value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="All">All types</option>
                    {meta.types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-[#1F1F1F]">
                    <input data-testid="incident-riddor-toggle" type="checkbox" checked={riddorOnly}
                        onChange={(e) => setRiddorOnly(e.target.checked)} />
                    RIDDOR only
                </label>
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                        <tr>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="reference" onClick={toggleSort}>Ref / Type</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="occurred_at" onClick={toggleSort}>Occurred</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="client" onClick={toggleSort}>Client / Building</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="location" onClick={toggleSort}>Location</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="severity" onClick={toggleSort}>Severity</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="status" onClick={toggleSort}>Status</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="riddor" onClick={toggleSort}>RIDDOR</SortableTh>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-10 text-[#9A968F]">
                                <Loader2 size={16} className="inline animate-spin mr-2" />Loading…</td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-10 text-[#9A968F]">No incidents match.</td></tr>
                        ) : sorted.map((r) => (
                            <tr key={r.id} data-testid={`incident-row-${r.id}`}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                onClick={() => setDialog(r)}>
                                <td className="px-4 py-3">
                                    <div className="font-mono text-[12px] text-[#9A968F]">{r.reference}</div>
                                    <div className="font-semibold text-[#1F1F1F]">{r.incident_type}</div>
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{(r.occurred_at || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">
                                    <div>{clientMap[r.client_id]?.name || "—"}</div>
                                    {r.building_id && <div className="text-[11px]">{buildingMap[r.building_id]?.name || ""}</div>}
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.location || "—"}</td>
                                <td className="px-4 py-3"><Pill tone={SEVERITY_TONE[r.severity] || "neutral"}>{r.severity}</Pill></td>
                                <td className="px-4 py-3"><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                                <td className="px-4 py-3">{r.riddor_reportable && <ShieldAlert size={15} className="text-red-600" />}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-1 justify-end">
                                        {canEdit && r.status !== "Closed" && (
                                            <button onClick={(e) => { e.stopPropagation(); closeIncident(r); }}
                                                data-testid={`incident-close-${r.id}`}
                                                className="text-[12px] text-[#3F7D3A] hover:underline inline-flex items-center gap-1">
                                                <Check size={12} /> Close
                                            </button>
                                        )}
                                        <AdminDeleteButton endpoint={`/incidents/${r.id}`} label="incident"
                                            testId={`incident-delete-${r.id}`} onDeleted={refresh} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </>)}

            {dialog === "new" && (
                <IncidentDialog
                    incident={null} meta={meta} clients={clients} buildings={buildings}
                    portalClientId={!isInternalView ? portalClientId : null}
                    isReviewer={user && INTERNAL_ROLES.has(user.role)}
                    prefill={newPrefill} backUrl={backUrl}
                    onReturnToProject={backUrl ? () => navigate(backUrl) : null}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); refresh(); if (backUrl) navigate(backUrl); }}
                />
            )}
            {dialog && dialog !== "new" && (
                <IncidentDialog
                    incident={dialog} meta={meta} clients={clients} buildings={buildings}
                    portalClientId={!isInternalView ? portalClientId : null}
                    canAskAi={user?.role !== "site_manager"}
                    isReviewer={user && INTERNAL_ROLES.has(user.role)}
                    backUrl={backUrl}
                    onReturnToProject={backUrl ? () => navigate(backUrl) : null}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); refresh(); if (backUrl) navigate(backUrl); }}
                />
            )}
        </div>
    );
}

export function IncidentDialog({ incident, meta, clients, buildings, portalClientId, isReviewer = false, onClose, onSaved, viewerCanEdit = true, canAskAi = true, prefill, onReturnToProject = null }) {
    const isNew = !incident;
    const [inc, setInc] = useState(incident);          // live server copy (for workflow state)
    const [tab, setTab] = useState("report");
    const [form, setForm] = useState(() => incident ? { ...incident } : {
        client_id: portalClientId || clients[0]?.id || "",
        building_id: "",
        incident_type: "Accident",
        severity: "Minor",
        occurred_at: new Date().toISOString().slice(0, 16),
        location: "",
        description: "",
        immediate_actions: "",
        root_cause: "",
        corrective_actions: "",
        reported_by_name: "",
        riddor_reportable: false,
        riddor_category: "Not reportable",
        status: "Reported",
        ...(prefill || {}),
    });
    const [invForm, setInvForm] = useState(() => ({ ...(incident?.investigation || {}) }));
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [typeChosen, setTypeChosen] = useState(!isNew);
    const [invBusy, setInvBusy] = useState(false);
    const [actionTitle, setActionTitle] = useState("");
    const [actionKind, setActionKind] = useState("Corrective");
    const navigate = useNavigate();
    const filteredBuildings = buildings.filter((b) => !form.client_id || b.client_id === form.client_id);
    const status = inc?.status || form.status;
    const isNearMiss = form.incident_type === "Near miss";
    const isPositive = form.incident_type === "Positive observation";
    const subtypes = (meta.event_category_taxonomy || {})[form.event_category] || [];

    // Keep the tab valid when Injured-person is hidden (near miss).
    useEffect(() => {
        if (isNearMiss && tab === "person") setTab("report");
    }, [isNearMiss, tab]);
    // Positive observations have no injury/investigation tabs.
    useEffect(() => {
        if (isPositive && (tab === "person" || tab === "investigation")) setTab("report");
    }, [isPositive, tab]);

    async function downloadPdf() {
        setDownloading(true);
        const t = toast.loading("Generating PDF… this can take a moment for reports with photos.");
        try {
            const resp = await api.get(`/incidents/${incident.id}/pdf`, { responseType: "blob", timeout: 180000 });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(resp, `${incident.reference || "incident"}.pdf`);
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success("PDF ready", { id: t });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "PDF generation failed. Please try again in a moment.", { id: t });
        }
        setDownloading(false);
    }

    function applyServer(data) {
        setInc(data);
        setForm((f) => ({ ...f, status: data.status }));
        setInvForm({ ...(data.investigation || {}) });
    }

    async function save() {
        if (!form.client_id) { toast.error("Client required"); return; }
        if (!(form.description || "").trim()) { toast.error("Description required"); return; }
        if (!form.occurred_at) { toast.error("Occurred date required"); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.occurred_at && payload.occurred_at.length === 16) {
                payload.occurred_at = payload.occurred_at + ":00.000Z";
            }
            const wasRiddorAutomated = !isNew && incident?.riddor_automation_done;
            if (isNew) {
                const { data } = await api.post("/incidents", payload);
                toast.success(data?.riddor_automation_done
                    ? "RIDDOR incident logged — investigation form, follow-up visit & action auto-created"
                    : "Incident reported");
            } else {
                const { data } = await api.patch(`/incidents/${incident.id}`, payload);
                toast.success(data?.riddor_automation_done && !wasRiddorAutomated
                    ? "RIDDOR flagged — investigation form, follow-up visit & action auto-created"
                    : "Incident updated");
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
        setSaving(false);
    }

    async function startInvestigation() {
        setInvBusy(true);
        try {
            const { data } = await api.post(`/incidents/${inc.id}/start-investigation`,
                { lead_name: invForm.lead_name || undefined, methodology: invForm.methodology || undefined });
            applyServer(data);
            toast.success("Investigation opened");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start"); }
        setInvBusy(false);
    }
    async function saveInvestigation() {
        setInvBusy(true);
        try {
            const { data } = await api.patch(`/incidents/${inc.id}/investigation`, {
                lead_name: invForm.lead_name, methodology: invForm.methodology,
                immediate_cause: invForm.immediate_cause, underlying_cause: invForm.underlying_cause,
                contributing_factors: invForm.contributing_factors, lessons_learned: invForm.lessons_learned,
                preventive_measures: invForm.preventive_measures, findings: invForm.findings,
                risk_assessment_reviewed: !!invForm.risk_assessment_reviewed,
            });
            applyServer(data);
            toast.success("Investigation saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setInvBusy(false);
    }
    async function completeInvestigation() {
        setInvBusy(true);
        try {
            // persist latest fields first so the server has the cause/measures
            await api.patch(`/incidents/${inc.id}/investigation`, {
                underlying_cause: invForm.underlying_cause, preventive_measures: invForm.preventive_measures,
                immediate_cause: invForm.immediate_cause, contributing_factors: invForm.contributing_factors,
                lessons_learned: invForm.lessons_learned, findings: invForm.findings,
                risk_assessment_reviewed: !!invForm.risk_assessment_reviewed,
                lead_name: invForm.lead_name, methodology: invForm.methodology,
            });
            const { data } = await api.post(`/incidents/${inc.id}/complete-investigation`);
            applyServer(data);
            toast.success("Investigation complete — pending sign-off");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not complete"); }
        setInvBusy(false);
    }

    async function createLinkedAction() {
        if (!actionTitle.trim()) { toast.error("Action title required"); return; }
        setSaving(true);
        try {
            await api.post(`/incidents/${incident.id}/create-action`, { title: actionTitle, priority: "High", kind: actionKind });
            toast.success(`${actionKind} action created`);
            setActionTitle("");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create action");
        }
        setSaving(false);
    }

    const TABS = [
        { id: "report", label: "Report", icon: ClipboardList },
        ...(!isNearMiss && !isPositive ? [{ id: "person", label: "Injured person", icon: UserRound }] : []),
        ...(!isPositive ? [{ id: "investigation", label: "Investigation", icon: Microscope }] : []),
        { id: "signoff", label: "Sign-off", icon: PenLine },
    ];

    // Upfront type chooser for a brand-new report.
    const TYPE_CARDS = [
        { key: "Near miss", icon: AlertTriangle, tone: "#C68A1C", blurb: "No injury — a close call to record and learn from." },
        { key: "Accident", icon: HardHat, tone: "#B33A2E", blurb: "Someone was hurt, or there was injury / ill health." },
        { key: "Incident", icon: ShieldAlert, tone: "#2A5DB0", blurb: "Dangerous occurrence, damage or environmental event." },
        { key: "Positive observation", icon: ThumbsUp, tone: "#3F7D3A", blurb: "Good practice or a positive H&S observation to record and share." },
    ];
    if (isNew && !typeChosen) {
        return (
            <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="incident-dialog">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                    <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                        <h3 className="text-base font-bold text-[#1F1F1F]">What are you reporting?</h3>
                        <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                    </div>
                    <div className="p-5 space-y-3">
                        <p className="text-[12.5px] text-[#6F6A64]">Choose the type of event. You can refine it later.</p>
                        {TYPE_CARDS.map((c) => (
                            <button key={c.key} data-testid={`incident-type-choice-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => { setForm({ ...form, incident_type: c.key }); setTypeChosen(true); }}
                                className="w-full flex items-start gap-3 text-left rounded-xl border border-[#E5E0DA] p-3.5 hover:border-[var(--brand-accent,_#C75A1B)] hover:bg-[#FCFBF9] transition-colors">
                                <span className="grid place-items-center w-10 h-10 rounded-lg shrink-0" style={{ background: c.tone + "1A" }}>
                                    <c.icon size={20} style={{ color: c.tone }} />
                                </span>
                                <span>
                                    <span className="block font-semibold text-[#1F1F1F] text-[14px]">{c.key}</span>
                                    <span className="block text-[12px] text-[#6F6A64] mt-0.5">{c.blurb}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="incident-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-start justify-between">
                    <div>
                        <h3 className="text-base font-bold text-[#1F1F1F] flex items-center gap-2">
                            {isNew ? "Report incident" : `Incident ${inc.reference}`}
                            {!isNew && <Pill tone={STATUS_TONE[status] || "neutral"} data-testid="incident-status-pill">{status}</Pill>}
                        </h3>
                        {!isNew && <div className="text-[11px] text-[#9A968F] mt-1">Reported {(inc.created_at || "").slice(0, 10)}</div>}
                    </div>
                    <div className="flex items-center gap-1">
                        {!isNew && (
                            <button data-testid="incident-download-pdf" onClick={downloadPdf} disabled={downloading}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#F6F4F0] text-[#1F1F1F] text-[11px] font-semibold hover:bg-[#EDE9E3] disabled:opacity-60" title="Download branded PDF report">
                                {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                            </button>
                        )}
                        {!isNew && canAskAi && (
                            <button data-testid={`incident-ask-ai-${incident.id}`}
                                onClick={() => navigate(`/app/ai-assist?context_type=incident&context_id=${incident.id}&context_label=${encodeURIComponent("Incident " + (incident.reference || ""))}`)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#EEF3FB] text-[#1F6FE5] text-[11px] font-semibold hover:bg-[#E0ECFB]" title="Ask Beyond AI about this incident">
                                <Sparkles className="w-3 h-3" /> Ask Beyond AI
                            </button>
                        )}
                        <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-5 pt-3 border-b border-[#E5E0DA] flex gap-1">
                    {TABS.map((t) => (
                        <button key={t.id} data-testid={`incident-tab-${t.id}`} onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[var(--brand-accent,#C75A1B)] text-[#1F1F1F]" : "border-transparent text-[#9A968F] hover:text-[#6F6A64]"}`}>
                            <t.icon size={13} /> {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-5 space-y-3 overflow-y-auto">
                    {/* ---------- REPORT TAB ---------- */}
                    {tab === "report" && (<>
                    {isPositive && (
                        <div data-testid="incident-positive-banner" className="rounded-xl border border-[#CDE6C9] bg-[#F2F8F1] p-3 flex items-start gap-2.5">
                            <ThumbsUp size={16} className="mt-0.5 shrink-0" style={{ color: "#3F7D3A" }} />
                            <p className="text-[12.5px] text-[#3F5A3B]">
                                Capturing good practice — record <b>what was observed</b>, where, who was involved and add photos.
                                No injury, RIDDOR or investigation details are needed.
                            </p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Client *">
                            <select data-testid="incident-client" value={form.client_id}
                                onChange={(e) => setForm({ ...form, client_id: e.target.value, building_id: "" })}
                                disabled={!!portalClientId || !isNew} className={inputCls}>
                                <option value="">— Select —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Building (optional)">
                            <select data-testid="incident-building" value={form.building_id || ""}
                                onChange={(e) => setForm({ ...form, building_id: e.target.value })} className={inputCls}>
                                <option value="">— None —</option>
                                {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Incident type *">
                            <select data-testid="incident-type" value={form.incident_type}
                                onChange={(e) => setForm({ ...form, incident_type: e.target.value })} className={inputCls}>
                                {meta.types.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Severity *">
                            <select data-testid="incident-severity" value={form.severity}
                                onChange={(e) => setForm({ ...form, severity: e.target.value })} className={inputCls}>
                                {meta.severities.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Occurred at *">
                            <input data-testid="incident-occurred" type="datetime-local"
                                value={(form.occurred_at || "").slice(0, 16)}
                                onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Location">
                            <input data-testid="incident-location" value={form.location || ""}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                                placeholder="e.g. Beacon Tower L17 east stair" className={inputCls} />
                        </Field>
                    </div>
                    <Field label="Description *">
                        <textarea data-testid="incident-description" rows={3} value={form.description || ""}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className={inputCls + " h-auto py-2"} />
                    </Field>
                    <Field label="Immediate actions taken">
                        <textarea rows={2} value={form.immediate_actions || ""}
                            onChange={(e) => setForm({ ...form, immediate_actions: e.target.value })}
                            className={inputCls + " h-auto py-2"} />
                    </Field>
                    {/* PPE + classification (merged from reporting form) */}
                    {!isPositive && (
                    <div className="rounded-xl border border-[#E5E0DA] p-3 space-y-3" data-testid="incident-circumstances">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Circumstances &amp; classification</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Field label="PPE worn?">
                                <select data-testid="incident-ppe-worn" value={form.ppe_worn || ""}
                                    onChange={(e) => setForm({ ...form, ppe_worn: e.target.value })} className={inputCls}>
                                    <option value="">— Select —</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                    <option value="N/A">N/A</option>
                                </select>
                            </Field>
                            <Field label="Type of PPE worn">
                                <input data-testid="incident-ppe-type" value={form.ppe_type || ""}
                                    onChange={(e) => setForm({ ...form, ppe_type: e.target.value })} className={inputCls} />
                            </Field>
                            <Field label="Time lost (hours)">
                                <input data-testid="incident-time-lost" type="number" min={0} step="0.5" value={form.time_lost_hours ?? ""}
                                    onChange={(e) => setForm({ ...form, time_lost_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                            </Field>
                        </div>
                        <Field label="Severity of incident (classification)">
                            <select data-testid="incident-severity-classification" value={form.severity_classification || ""}
                                onChange={(e) => setForm({ ...form, severity_classification: e.target.value })} className={inputCls}>
                                <option value="">— Select —</option>
                                {(meta.severity_classifications || []).map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Type of incident (category)">
                                <select data-testid="incident-event-category" value={form.event_category || ""}
                                    onChange={(e) => setForm({ ...form, event_category: e.target.value, event_subtype: "" })} className={inputCls}>
                                    <option value="">— Select —</option>
                                    {(meta.event_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </Field>
                            <Field label="Sub-type">
                                {subtypes.length > 0 ? (
                                    <select data-testid="incident-event-subtype" value={form.event_subtype || ""}
                                        onChange={(e) => setForm({ ...form, event_subtype: e.target.value })} className={inputCls}>
                                        <option value="">— Select —</option>
                                        {subtypes.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : (
                                    <input data-testid="incident-event-subtype" value={form.event_subtype || ""}
                                        onChange={(e) => setForm({ ...form, event_subtype: e.target.value })}
                                        placeholder="Optional detail" className={inputCls} />
                                )}
                            </Field>
                        </div>
                    </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Reported by">
                            <input value={form.reported_by_name || ""}
                                onChange={(e) => setForm({ ...form, reported_by_name: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Witnesses">
                            <input value={form.witnesses || ""}
                                onChange={(e) => setForm({ ...form, witnesses: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Status">
                            <select data-testid="incident-status-select" value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                                {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>
                    {/* RIDDOR */}
                    {!isPositive && (
                    <div className="rounded-xl border border-[#E5E0DA] p-3 space-y-2.5">
                        <label className="flex items-center gap-2 text-[13px] font-semibold text-[#1F1F1F]">
                            <input data-testid="incident-riddor" type="checkbox" checked={!!form.riddor_reportable}
                                onChange={(e) => setForm({ ...form, riddor_reportable: e.target.checked })} />
                            <ShieldAlert size={14} className="text-red-600" /> RIDDOR reportable
                        </label>
                        {form.riddor_reportable && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <Field label="RIDDOR category">
                                    <select data-testid="incident-riddor-category" value={form.riddor_category || "Not reportable"}
                                        onChange={(e) => setForm({ ...form, riddor_category: e.target.value })} className={inputCls}>
                                        {(meta.riddor_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </Field>
                                <Field label="HSE reference">
                                    <input data-testid="incident-riddor-ref" placeholder="F2508 ref"
                                        value={form.riddor_reference || ""}
                                        onChange={(e) => setForm({ ...form, riddor_reference: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Date reported to HSE">
                                    <input type="date" value={form.riddor_reported_date || ""}
                                        onChange={(e) => setForm({ ...form, riddor_reported_date: e.target.value })} className={inputCls} />
                                </Field>
                            </div>
                        )}
                    </div>
                    )}

                    {!isNew && inc?.riddor_automation_done && (
                        <div data-testid="riddor-automation-panel" className="rounded-xl border border-[#F0D9C9] bg-[#FCF4EE] p-3.5">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "#9F4212" }}>
                                <Zap size={13} /> RIDDOR automation — follow-ups generated
                            </h4>
                            <div className="grid sm:grid-cols-3 gap-2">
                                <button type="button" data-testid="riddor-link-investigation" disabled={!inc.linked_instance_id}
                                    onClick={() => inc.linked_instance_id && navigate(`/app/inspections/${inc.linked_instance_id}`)}
                                    className="flex items-center gap-2 text-left rounded-lg border border-[#E8D5C7] bg-white px-3 py-2 hover:border-[var(--brand-accent,_#C75A1B)] disabled:opacity-50">
                                    <ClipboardList size={15} style={{ color: "#9F4212" }} />
                                    <span className="text-[11.5px] font-semibold text-[#1F1F1F] leading-tight">Investigation form<br /><span className="font-normal text-[#9A968F]">Draft · ACC-INV</span></span>
                                </button>
                                <button type="button" data-testid="riddor-link-visit" disabled={!inc.linked_visit_id}
                                    onClick={() => navigate("/app/schedule")}
                                    className="flex items-center gap-2 text-left rounded-lg border border-[#E8D5C7] bg-white px-3 py-2 hover:border-[var(--brand-accent,_#C75A1B)] disabled:opacity-50">
                                    <CalendarClock size={15} style={{ color: "#9F4212" }} />
                                    <span className="text-[11.5px] font-semibold text-[#1F1F1F] leading-tight">Follow-up visit<br /><span className="font-normal text-[#9A968F]">Scheduled · +7 days</span></span>
                                </button>
                                <button type="button" data-testid="riddor-link-action"
                                    onClick={() => navigate("/app/actions")}
                                    className="flex items-center gap-2 text-left rounded-lg border border-[#E8D5C7] bg-white px-3 py-2 hover:border-[var(--brand-accent,_#C75A1B)]">
                                    <ListChecks size={15} style={{ color: "#9F4212" }} />
                                    <span className="text-[11.5px] font-semibold text-[#1F1F1F] leading-tight">Corrective action<br /><span className="font-normal text-[#9A968F]">High · due +3 days</span></span>
                                </button>
                            </div>
                        </div>
                    )}

                    {!isNew && <PhotoSection incident={inc} onChanged={async () => { try { const { data } = await api.get(`/incidents/${incident.id}`); applyServer(data); } catch { /* best-effort */ } }} canEdit={viewerCanEdit} />}

                    {!isNew && viewerCanEdit && (
                        <div className="border-t border-[#E5E0DA] pt-4 mt-4">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2 flex items-center gap-1">
                                <ListChecks size={12} /> Create linked action
                            </h4>
                            <div className="flex items-center gap-2">
                                <select data-testid="incident-action-kind" value={actionKind}
                                    onChange={(e) => setActionKind(e.target.value)}
                                    className="h-9 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                                    <option value="Corrective">Corrective</option>
                                    <option value="Preventative">Preventative</option>
                                    <option value="Investigation">Investigation</option>
                                </select>
                                <input data-testid="incident-action-title" placeholder="e.g. Replace fire door retainer in plant room 3"
                                    value={actionTitle} onChange={(e) => setActionTitle(e.target.value)}
                                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm flex-1" />
                                <button data-testid="incident-action-create" disabled={saving || !actionTitle.trim()}
                                    onClick={createLinkedAction}
                                    className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
                                    style={{ background: "#2A5DB0" }}>
                                    <Plus size={13} /> Create
                                </button>
                            </div>
                            {inc.linked_action_ids?.length > 0 && (
                                <p className="text-[11px] text-[#9A968F] mt-2">
                                    <FileText size={11} className="inline" /> {inc.linked_action_ids.length} linked action(s) on file
                                </p>
                            )}
                        </div>
                    )}
                    </>)}

                    {/* ---------- INJURED PERSON TAB ---------- */}
                    {tab === "person" && (
                        <div data-testid="incident-person-tab" className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Injured / affected person">
                                    <input data-testid="incident-injured-name" value={form.injured_person_name || ""}
                                        onChange={(e) => setForm({ ...form, injured_person_name: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Status">
                                    <select data-testid="incident-injured-status" value={form.injured_person_status || ""}
                                        onChange={(e) => setForm({ ...form, injured_person_status: e.target.value })} className={inputCls}>
                                        <option value="">— Select —</option>
                                        {(meta.injured_person_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </Field>
                                <Field label="Occupation / role">
                                    <input value={form.injured_person_occupation || ""}
                                        onChange={(e) => setForm({ ...form, injured_person_occupation: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Subcontractor (if applicable)">
                                    <input data-testid="incident-subcontractor-name" value={form.subcontractor_name || ""}
                                        onChange={(e) => setForm({ ...form, subcontractor_name: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Address">
                                    <input value={form.injured_person_address || ""}
                                        onChange={(e) => setForm({ ...form, injured_person_address: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Date of birth">
                                    <input type="date" value={form.injured_person_dob || ""}
                                        onChange={(e) => setForm({ ...form, injured_person_dob: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Nature of injury">
                                    <select data-testid="incident-injury-nature" value={form.injury_nature || ""}
                                        onChange={(e) => setForm({ ...form, injury_nature: e.target.value })} className={inputCls}>
                                        <option value="">— Select —</option>
                                        {(meta.injury_natures || []).map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </Field>
                                <Field label="Body part affected">
                                    <select data-testid="incident-body-part" value={form.body_part || ""}
                                        onChange={(e) => setForm({ ...form, body_part: e.target.value })} className={inputCls}>
                                        <option value="">— Select —</option>
                                        {(meta.body_parts || []).map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </Field>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                                <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F] pb-2">
                                    <input data-testid="incident-hospital" type="checkbox" checked={!!form.hospital_attended}
                                        onChange={(e) => setForm({ ...form, hospital_attended: e.target.checked })} /> Hospital attended
                                </label>
                                <Field label="Days absent from work">
                                    <input data-testid="incident-days-absent" type="number" min={0} value={form.days_absent ?? 0}
                                        onChange={(e) => setForm({ ...form, days_absent: parseInt(e.target.value || "0", 10) })} className={inputCls} />
                                </Field>
                                <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F] pb-2">
                                    <input type="checkbox" checked={!!form.first_aid_given}
                                        onChange={(e) => setForm({ ...form, first_aid_given: e.target.checked })} /> First aid given
                                </label>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="First aider name">
                                    <input value={form.first_aider_name || ""}
                                        onChange={(e) => setForm({ ...form, first_aider_name: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Treatment given">
                                    <input value={form.treatment_given || ""}
                                        onChange={(e) => setForm({ ...form, treatment_given: e.target.value })} className={inputCls} />
                                </Field>
                            </div>
                            <div className="border-t border-[#E5E0DA] pt-3">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Accident detail</h4>
                                <div className="grid grid-cols-1 gap-3">
                                    <Field label="Activity at the time">
                                        <input value={form.activity_at_time || ""}
                                            onChange={(e) => setForm({ ...form, activity_at_time: e.target.value })} className={inputCls} />
                                    </Field>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field label="Equipment / plant involved">
                                            <input value={form.equipment_involved || ""}
                                                onChange={(e) => setForm({ ...form, equipment_involved: e.target.value })} className={inputCls} />
                                        </Field>
                                        <Field label="Contributing conditions">
                                            <input value={form.contributing_conditions || ""}
                                                onChange={(e) => setForm({ ...form, contributing_conditions: e.target.value })} className={inputCls} />
                                        </Field>
                                    </div>
                                </div>
                            </div>
                            <div className="border-t border-[#E5E0DA] pt-3" data-testid="incident-classification">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Classification &amp; metrics</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <Field label="Accident category">
                                        <select data-testid="incident-accident-category" value={form.accident_category || ""}
                                            onChange={(e) => setForm({ ...form, accident_category: e.target.value })} className={inputCls}>
                                            <option value="">— Auto —</option>
                                            {(meta.accident_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Shift">
                                        <select data-testid="incident-shift" value={form.shift || ""}
                                            onChange={(e) => setForm({ ...form, shift: e.target.value })} className={inputCls}>
                                            <option value="">— Select —</option>
                                            {(meta.shifts || []).map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Department">
                                        <input data-testid="incident-department" value={form.department || ""}
                                            onChange={(e) => setForm({ ...form, department: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Initial cause">
                                        <select data-testid="incident-initial-cause" value={form.initial_cause || ""}
                                            onChange={(e) => setForm({ ...form, initial_cause: e.target.value })} className={inputCls}>
                                            <option value="">— Select —</option>
                                            {(meta.initial_causes || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Root cause category">
                                        <select data-testid="incident-root-cause" value={form.root_cause_category || ""}
                                            onChange={(e) => setForm({ ...form, root_cause_category: e.target.value })} className={inputCls}>
                                            <option value="">— Select —</option>
                                            {(meta.root_cause_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Unsafe act / condition">
                                        <select data-testid="incident-unsafe" value={form.unsafe_classification || ""}
                                            onChange={(e) => setForm({ ...form, unsafe_classification: e.target.value })} className={inputCls}>
                                            <option value="">— Select —</option>
                                            {(meta.unsafe_classifications || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <div className="flex items-center gap-6 mt-3">
                                    <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                                        <input data-testid="incident-machinery" type="checkbox" checked={!!form.machinery_involved}
                                            onChange={(e) => setForm({ ...form, machinery_involved: e.target.checked })} /> Machinery / equipment involved
                                    </label>
                                    <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                                        <input data-testid="incident-claim" type="checkbox" checked={!!form.claim_received}
                                            onChange={(e) => setForm({ ...form, claim_received: e.target.checked })} /> Claim received
                                    </label>
                                </div>
                            </div>
                            <div className="border-t border-[#E5E0DA] pt-3" data-testid="incident-injured-signature">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Injured person signature</h4>
                                <SignaturePad value={form.injured_person_signature || ""}
                                    onChange={(v) => setForm({ ...form, injured_person_signature: v })}
                                    label="Injured person" />
                            </div>
                        </div>
                    )}

                    {/* ---------- INVESTIGATION TAB ---------- */}
                    {tab === "investigation" && (
                        <div data-testid="incident-investigation-tab" className="space-y-3">
                            {isNew ? (
                                <div className="text-[12.5px] text-[#9A968F] bg-[#F5F3F0] rounded-lg p-4 text-center">
                                    Save the incident first, then open it to run an investigation.
                                </div>
                            ) : (<>
                                {/* Workflow status banner */}
                                <div className="rounded-xl border border-[#E5E0DA] p-3.5 flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2 text-[12.5px]">
                                        <Pill tone={STATUS_TONE[status] || "neutral"}>{status}</Pill>
                                        {inc?.investigation?.completed_by_name && (
                                            <span className="text-[#6F6A64]">Completed by <b>{inc.investigation.completed_by_name}</b></span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {status === "Reported" && viewerCanEdit && (
                                            <button data-testid="incident-start-investigation" onClick={startInvestigation} disabled={invBusy}
                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "#2A5DB0" }}>
                                                {invBusy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Start investigation
                                            </button>
                                        )}
                                        {status === "Investigating" && isReviewer && (
                                            <button data-testid="incident-complete-investigation" onClick={completeInvestigation} disabled={invBusy}
                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "#3F7D3A" }}>
                                                {invBusy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Complete investigation
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {status === "Reported" ? (
                                    <div className="text-[12.5px] text-[#9A968F] bg-[#F5F3F0] rounded-lg p-4 text-center">
                                        No investigation open. Assign a lead and methodology below, then start.
                                    </div>
                                ) : null}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Investigation lead">
                                        <input data-testid="inv-lead" value={invForm.lead_name || ""}
                                            onChange={(e) => setInvForm({ ...invForm, lead_name: e.target.value })} className={inputCls} />
                                    </Field>
                                    <Field label="Methodology">
                                        <select data-testid="inv-methodology" value={invForm.methodology || ""}
                                            onChange={(e) => setInvForm({ ...invForm, methodology: e.target.value })} className={inputCls}>
                                            <option value="">— Select —</option>
                                            {(meta.investigation_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Immediate cause (what happened)">
                                    <textarea rows={2} value={invForm.immediate_cause || ""}
                                        onChange={(e) => setInvForm({ ...invForm, immediate_cause: e.target.value })} className={inputCls + " h-auto py-2"} />
                                </Field>
                                <Field label="Underlying / root cause *">
                                    <textarea data-testid="inv-underlying-cause" rows={2} value={invForm.underlying_cause || ""}
                                        onChange={(e) => setInvForm({ ...invForm, underlying_cause: e.target.value })} className={inputCls + " h-auto py-2"} />
                                </Field>
                                <Field label="Contributing factors">
                                    <textarea rows={2} value={invForm.contributing_factors || ""}
                                        onChange={(e) => setInvForm({ ...invForm, contributing_factors: e.target.value })} className={inputCls + " h-auto py-2"} />
                                </Field>
                                <Field label="Preventive / corrective measures *">
                                    <textarea data-testid="inv-preventive-measures" rows={2} value={invForm.preventive_measures || ""}
                                        onChange={(e) => setInvForm({ ...invForm, preventive_measures: e.target.value })} className={inputCls + " h-auto py-2"} />
                                </Field>
                                <Field label="Lessons learned">
                                    <textarea rows={2} value={invForm.lessons_learned || ""}
                                        onChange={(e) => setInvForm({ ...invForm, lessons_learned: e.target.value })} className={inputCls + " h-auto py-2"} />
                                </Field>
                                <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                                    <input data-testid="inv-ra-reviewed" type="checkbox" checked={!!invForm.risk_assessment_reviewed}
                                        onChange={(e) => setInvForm({ ...invForm, risk_assessment_reviewed: e.target.checked })} />
                                    Risk assessment reviewed / updated following this incident
                                </label>
                                {viewerCanEdit && status !== "Reported" && status !== "Closed" && (
                                    <button data-testid="inv-save" onClick={saveInvestigation} disabled={invBusy}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold border border-[#E5E0DA] bg-white">
                                        {invBusy ? <Loader2 size={13} className="animate-spin" /> : <ClipboardCheck size={13} />} Save investigation
                                    </button>
                                )}
                            </>)}
                        </div>
                    )}

                    {/* ---------- SIGN-OFF TAB ---------- */}
                    {tab === "signoff" && (
                        <div data-testid="incident-signoff-tab" className="space-y-3">
                            <p className="text-[12.5px] text-[#6F6A64]">Details of the person completing this form.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <Field label="Name (print)">
                                    <input data-testid="signoff-name" value={form.signoff_name || ""}
                                        onChange={(e) => setForm({ ...form, signoff_name: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Position">
                                    <input data-testid="signoff-position" value={form.signoff_position || ""}
                                        onChange={(e) => setForm({ ...form, signoff_position: e.target.value })} className={inputCls} />
                                </Field>
                                <Field label="Date">
                                    <input data-testid="signoff-date" type="date" value={form.signoff_date || ""}
                                        onChange={(e) => setForm({ ...form, signoff_date: e.target.value })} className={inputCls} />
                                </Field>
                            </div>
                            <div className="rounded-xl border border-[#E5E0DA] p-3">
                                <SignaturePad value={form.signoff_signature || ""}
                                    onChange={(v) => setForm({ ...form, signoff_signature: v })}
                                    label="Signature" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-between gap-2 bg-[#F5F3F0]">
                    <div>
                        {onReturnToProject && (
                            <button data-testid="incident-return-to-project" onClick={onReturnToProject}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#3e4b63] hover:text-[#1F1F1F]">
                                <ArrowLeft size={14} /> Return to project
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button data-testid="incident-close-dialog" onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Close</button>
                        {viewerCanEdit && (
                            <button data-testid="incident-save" onClick={save} disabled={saving}
                                className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isNew ? (onReturnToProject ? "Report & return" : "Report") : (onReturnToProject ? "Save & return" : "Save")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PhotoSection({ incident, onChanged, canEdit }) {
    const [photos, setPhotos] = useState(incident?.photos || []);
    const [lightbox, setLightbox] = useState(null);
    const isLocked = incident?.status === "Closed";
    const editable = canEdit && !isLocked;

    useEffect(() => { setPhotos(incident?.photos || []); }, [incident]);

    const { pendingPhotos, queuePhoto: queuePhotoOnly, retry: retryQueuedPhoto, discard: discardQueuedPhoto, discardAll: discardAllPendingPhotos } = useOfflinePhotoQueue({
        entityType: "incident", entityId: incident?.id, enabled: !!incident?.id,
        recordLabel: incident ? `Incident — ${incident.location || incident.incident_type || "Untitled"}` : undefined,
        onUploaded: onChanged,
    });

    async function upload(files) {
        if (!files || files.length === 0) return;
        let queuedCount = 0;
        for (const file of files) {
            try { await queuePhotoOnly(file, {}); queuedCount += 1; }
            catch (e) { toast.error(`Could not save ${file.name} on this device`); }
        }
        if (!queuedCount) return;
        if (navigator.onLine) toast.success(queuedCount > 1 ? `${queuedCount} photos added — uploading…` : "Photo added — uploading…");
        else toast.info(queuedCount > 1 ? `${queuedCount} photos saved on this device — will upload automatically` : "Photo saved on this device — will upload automatically");
    }

    async function removePhoto(photo) {
        if (!window.confirm("Delete this photo?")) return;
        try {
            await api.delete(`/incidents/${incident.id}/photos/${photo.id}`);
            setPhotos((p) => p.filter((x) => x.id !== photo.id));
            toast.success("Photo removed");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    }

    function photoUrl(photo) {
        const base = process.env.REACT_APP_BACKEND_URL;
        return `${base}/api/incidents/${incident.id}/photos/${photo.id}`;
    }

    return (
        <div className="border-t border-[#E5E0DA] pt-4 mt-4" data-testid="incident-photos-section">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1">
                    <Camera size={12} /> Photos &amp; evidence ({photos.length})
                </h4>
                {editable && (
                    <label className="cursor-pointer inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-lg text-white"
                        style={{ background: "#2A5DB0" }}>
                        <Plus size={12} />
                        Add photo
                        <input data-testid="incident-photo-upload" type="file" accept="image/*" multiple className="hidden"
                            onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
                    </label>
                )}
            </div>
            <OtherDevicePendingBanner pendingUploads={incident?.pending_uploads} canClear={editable} testIdPrefix="incident-pending-uploads-banner"
                onClear={async (devId) => { await clearOtherDevicePending("incident", incident.id, devId); await onChanged?.(); }} />
            {photos.length === 0 ? (
                <div className="text-[12px] text-[#9A968F] py-3 text-center bg-[#F5F3F0] rounded-lg">
                    No photos attached yet.
                </div>
            ) : (
                <div className="grid grid-cols-4 gap-2">
                    {photos.map((p) => (
                        <div key={p.id} data-testid={`incident-photo-${p.id}`}
                            className="relative group rounded-lg overflow-hidden border border-[#E5E0DA] bg-[#F5F3F0] aspect-square">
                            <img src={photoUrl(p)} alt={p.caption || p.filename}
                                onClick={() => setLightbox(p)}
                                className="w-full h-full object-cover cursor-zoom-in transition group-hover:scale-105" />
                            {editable && (
                                <button onClick={() => removePhoto(p)} data-testid={`incident-photo-delete-${p.id}`}
                                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
                                    <Trash2 size={11} />
                                </button>
                            )}
                            {p.caption && (
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{p.caption}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {pendingPhotos.length > 0 && (
                <div data-testid="incident-photo-sync-status" className="flex items-center gap-2 rounded-lg px-2.5 py-2 border mt-2" style={{ background: "#FBEFE3", borderColor: "#F0DCC2" }}>
                    <CloudOff size={13} className="text-[#9F4212] shrink-0" />
                    <p className="text-[11.5px] text-[#9F4212] font-semibold flex-1">
                        {pendingPhotos.length} photo{pendingPhotos.length !== 1 ? "s" : ""} {pendingPhotos.every((p) => p.failed) ? "failed to upload — tap below to retry or discard." : "saved on this device — uploading…"}
                    </p>
                    {editable && (
                        <button type="button" data-testid="incident-discard-all-pending-photos"
                                onClick={() => { if (window.confirm(`Discard all ${pendingPhotos.length} pending photo(s)? They will not be uploaded.`)) discardAllPendingPhotos(); }}
                                className="text-[11px] font-semibold text-[#9F4212] hover:underline shrink-0">
                            Discard all
                        </button>
                    )}
                </div>
            )}
            <PendingPhotoThumbs pending={pendingPhotos} testId="incident-pending-photos" onRetry={retryQueuedPhoto} onDiscard={editable ? discardQueuedPhoto : null} />

            {lightbox && (
                <div className="fixed inset-0 z-[80] bg-black/80 grid place-items-center p-6" onClick={() => setLightbox(null)}>
                    <img src={photoUrl(lightbox)} alt={lightbox.caption || lightbox.filename}
                        className="max-h-full max-w-full rounded-lg shadow-2xl" />
                </div>
            )}
        </div>
    );
}
